/**
 * useCountUp — React-driven count-up animation for headline numbers.
 *
 * Replaces the imperative `animateNumberValue` paint in
 * `lib/pilot/format.ts` for component-rendered targets. Returns the
 * formatted current value as a string; the consumer plugs that into
 * JSX directly.
 *
 * Behavior:
 *   - Holds at the formatted final value once the ref enters the
 *     viewport (≥ `threshold` ratio visible) and then runs a
 *     requestAnimationFrame ease-out from 0 → target over `duration`
 *     ms. The first run "arms" — subsequent target changes (e.g.,
 *     knob drag re-runs the simulation) update instantly without a
 *     fresh count-up, matching the old story-render.js behavior
 *     (`_winsArmed` flag).
 *   - Respects `prefers-reduced-motion`: visitor with the OS flag
 *     skips the animation and gets the final value immediately.
 *   - `format` is called on every frame and on the final value.
 *
 * Built for Phase C of the engine-imperative retirement
 * (docs/eng/pilot-engine-imperative-retirement.md) — SandboxHeroWins
 * + SandboxTodaysTax in Sandbox.tsx.
 */

import {useEffect, useRef, useState, type RefObject} from 'react';

export interface UseCountUpOptions {
  /** Animation duration in milliseconds. Default 900. */
  duration?: number;
  /** IntersectionObserver threshold to "arm" the count-up. Default 0.35. */
  threshold?: number;
}

export function useCountUp(
  target: number,
  format: (n: number) => string,
  ref: RefObject<HTMLElement | null>,
  options: UseCountUpOptions = {},
): string {
  const {duration = 900, threshold = 0.35} = options;
  const [display, setDisplay] = useState(() => format(0));
  const armedRef = useRef(false);
  const lastTargetRef = useRef<number | null>(null);

  // Watch the ref for viewport entry. Once it enters, arm the
  // count-up for the *current* target value.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof IntersectionObserver === 'undefined') {
      // SSR or very old browser — show the final value immediately.
      setDisplay(format(target));
      return;
    }
    const el = ref.current;
    if (!el) return;

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            armedRef.current = true;
          }
        }
      },
      {threshold},
    );
    io.observe(el);

    // If the element is already in viewport at mount, fire immediately.
    const rect = el.getBoundingClientRect();
    const inView =
      rect.top < window.innerHeight * (1 - threshold) && rect.bottom > 0;
    if (inView) armedRef.current = true;

    if (armedRef.current && prefersReduced) {
      setDisplay(format(target));
    }

    return () => io.disconnect();
  }, [ref, threshold, target, format]);

  // Animate to target whenever target changes AND we're armed.
  useEffect(() => {
    if (!armedRef.current) {
      // Not armed yet — but if the visitor lands below the tile (e.g.
      // a scroll-jumped deep link, or a section moved off-viewport on a
      // re-layout) the IntersectionObserver may never fire. Surface the
      // current target as the display so the visitor doesn't see a
      // stale "$0" / "0 hrs" stuck above the fold.
      //
      // Per the May 2026 functional/content review: visitors were
      // landing on the Sandbox portfolio rollup and seeing "0" placeholder
      // numbers because useCountUp's armed-once gate held the display at
      // format(0). Track the target as the source of truth instead.
      lastTargetRef.current = target;
      setDisplay(format(target));
      return;
    }

    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const startTarget = lastTargetRef.current ?? 0;
    lastTargetRef.current = target;

    if (prefersReduced || startTarget === target) {
      setDisplay(format(target));
      return;
    }

    let raf = 0;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const v = startTarget + (target - startTarget) * eased;
      setDisplay(format(v));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [target, duration, format]);

  return display;
}
